import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Flex, useDisclosure } from '@chakra-ui/react';
import { useReactToPrint } from 'react-to-print';

import {
  AsideCollapse,
  CASTracker,
  CustomHeading,
  Modal,
  hasAgentExcludingEnroly,
  offerVisaSupport,
  Navbar,
  NavbarActionButtons,
  SidebarCollapse,
  setUserAlias,
  removeUserAlias,
  getUserAlias,
} from 'shared-components';

import { StudentState } from '../../../store/student/types';
import StudentHeader from '../../molecules/studentHeader';
import StudentLeftNav from '../../molecules/studentLeftNav';
import PageWrapper from '../../templates/dashboard-wrap';
import Checklist from './checklist/container';
import Documents from './documents/container';
import FinanceDocuments from './financialDocs/container';
import Info from './info';
import Interview from './interview/container';
import ScheduledInterviews from './scheduledInterviews/container';

import { AuthState, UserRole } from '../../../store/auth/types';
import { IOpenedStudentsRecord } from '../../../store/dashboard/types';
import { Status, VisaStatus } from '../../../store/student/core/types';
import { IArrivalStatus, IConfigState, IDate, IRisk, ISponsorDetails } from '../../../types';
import { boolToYesNo, formatCurrency } from '../../../utilities/formatting';
import { P, hasPerm } from '../../../utilities/roles/permissions';
import { SectionInfoState, SectionProps } from '../../molecules/studentLeftNav/types';
import Finances from '../../sections/finance';
import PriorStudyAndTravel from '../../sections/history/prior_study_and_travel';
import PriorTravelAndRefusals from '../../sections/history/prior_travel_and_refusals';
import UpcomingVisaAndDependants from '../../sections/history/upcoming_visa_and_dependants';
import ContactAndAddress from '../../sections/personal_info/contact_and_address';
import CourseDetailsAndDeposit from '../../sections/personal_info/course_details_and_deposit';
import CriminalConvictionAndDisability from '../../sections/personal_info/criminal_convictions_and_disability';
import EnglishTestPI from '../../sections/personal_info/english_test';
import EnrolmentSupport from '../../sections/personal_info/enrolment_support';
import NameAndDOB from '../../sections/personal_info/name_and_dob';
import NationalityAndSex from '../../sections/personal_info/nationality_and_sex';
import PassportDetails from '../../sections/personal_info/passport_details';
import VisaSupport from '../../sections/personal_info/visa_support';
import AgentDetailsSummary from '../../sections/agent_details';
import WhatsApp from '../../sections/personal_info/whatsApp';
import VisaPrepPreview from '../../sections/visaPrep';
import AgentAccessModal from '../modals/agentAccess';
import EditDateModal from '../modals/editDate';
import EditVisaStatusModal from '../modals/editVisaStatus';
import VisaPrepActivate from '../modals/visaPrepActivate/container';
import { AdminActions } from './admin-actions';
import CASTrackerReminder from './casTrackerReminder';
import VisaPrepForm from './visaPrepForm';
import { programmableTagsActions } from '../../../store/programmable_tags/reducer';

import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { EventInfoStatus, IMessage, ITag, RecordedInterviewSystemStatus } from 'api-paths-cas';
import { logout } from '../../../store/auth/actions';
import { useSession } from 'shared-components/authentication';
import { getT4VisaTime as getT4VisaTimeUtils } from '../../../utilities/T4VisaTime';
import { MainColumn } from '../../molecules/MainColumn';
import StudentActions from '../../molecules/studentActions';
import StudentSidebar from '../../molecules/studentSidebar';
import { EditArrivalStatus } from '../modals/editArrivalStatus';
import { Auditlog } from './auditlog';
import AfterTravelDocumentsDashboard from './followUpDocs/after/container';
import BeforeTravelDocumentsDashboard from './followUpDocs/before/container';
import { closeStudent, getStudentDocs, getStudentMessages, registerStudentView } from '../../../store/student/actions';
import { setStudentConfigLoaded } from '../../../store/data/actions';
import { ApplicationState } from '../../../store';
import { selectIntakeAfterCutoff } from '../../../store/selectors';
import { requestStudents } from '@/store/dashboard/actions';
import { useStudentViewRegistration } from '../../../hooks/useStudentViewRegistration';
import { getAdmAllowedAliases } from '../../../store/config/actions';

export interface IDispatchProps {
  getStudent: (id: string, university: string, intakeId: string, offerId: string) => void;
  getAuditLog: (id: string, university: string, intakeId: string, offerId: string) => void;
  onUpdateStatus: (id: string, status: Status) => void;
  onSubmitMessage: (message: IMessage) => void;
  onOverrideMaturedDate: (date: IDate) => void;
  onOverrideVisaStatus: (status: VisaStatus) => void;
  onOverrideArrivalStatus: (arrivalStatus: IArrivalStatus) => void;
  markMessagesRead: (messageIds: Array<string>) => void;
  markMessageUnread: (messageId: string) => void;
  onDownload: (includeHidden?: boolean) => void;
  unpinTagsFromStudents: ({
    tags,
    studentNumbers,
  }: {
    tags: { id: string; isProgrammable: boolean }[];
    studentNumbers: string[];
  }) => void;
  pinTagsToStudents: ({
    tags,
    studentNumbers,
  }: {
    tags: { id: string; isProgrammable: boolean }[];
    studentNumbers: string[];
  }) => void;
}

export interface IStateProps {
  lockAgent?: boolean;
  student: StudentState;
  config: IConfigState;
  auth: AuthState;
  isLoading: boolean;
  isSocketUp: boolean;
  sections: SectionProps[];
  infoState: SectionInfoState;
  checklistComplete: number;
  docsComplete: number;
  docsRequired: number;
  finDocsComplete: number;
  finDocsRequired: number;
  beforeFollowUpDocsComplete: number;
  beforeFollowUpDocsRequired: number;
  afterFollowUpDocsComplete: number;
  afterFollowUpDocsRequired: number;
  interviewComplete: number;
  visaPrepComplete: number;
  isConfidenceScoreView: boolean;
  isChecklistRisk: boolean;
  isInactiveChecklistRisk: boolean;
  isDocsDownloadsActive: boolean;
  isDocsRisk: boolean;
  isInactiveDocRisk: boolean;
  isFinDocsRisk: boolean;
  isInactiveFinDocRisk: boolean;
  isPreCasRisk: boolean;
  isInactivePreCasRisk: boolean;
  isUnlockVisaPrep: boolean;
  isPathway: boolean;
  preCasFlags: number;
  userName: string;
  displayName: string;
  successPercentage?: number;
  riskList: IRisk;
  openedStudentRecords: ReadonlyArray<IOpenedStudentsRecord>;
  hasVisaPrepBeenActivated: boolean;
  isStudentLoaded: boolean;
  userRole: UserRole;
  groups: Array<string>;
  visaAssistance: boolean;
  isAgent: boolean;
  showLastTouch: boolean;
  showArrivalStatus: boolean;
  travelStatusSection: IArrivalStatus;
  showAgent: boolean;
  hasAgentMessages: boolean;
  isPreDepartureDocsHidden: boolean;
  isAfterTravelDocsHidden: boolean;
  isPreCasFormHidden: boolean;
  isGeneralDocFormHidden: boolean;
  isFinanceDocFormHidden: boolean;
  isScheduledInterviewsHidden: boolean;
  isRecordedInterviewHidden: boolean;
  isTrackerVisible: boolean;
  allTags: ITag[];
  allProgrammableTags: ITag[];
  calendlyInterviewsInfoStatus: EventInfoStatus;
  recordedInterviewSystemStatus: RecordedInterviewSystemStatus;
  processedByUni?: boolean;
  allowedAliases: string[];
  currentUser?: { givenName?: string; familyName?: string };
}

const useLoadStudents = () => {
  const [searchParams] = useSearchParams();
  useEffect(() => {
    requestStudents();
  }, [searchParams]);
};

type IProps = IDispatchProps & IStateProps;

function Dashboard(props: IProps) {
  const {
    lockAgent,
    student,
    isLoading,
    infoState,
    checklistComplete,
    docsComplete,
    docsRequired,
    finDocsComplete,
    finDocsRequired,
    beforeFollowUpDocsComplete,
    beforeFollowUpDocsRequired,
    afterFollowUpDocsComplete,
    afterFollowUpDocsRequired,
    interviewComplete,
    visaPrepComplete,
    isChecklistRisk,
    isInactiveChecklistRisk,
    isConfidenceScoreView,
    isDocsDownloadsActive,
    isDocsRisk,
    isInactiveDocRisk,
    isFinDocsRisk,
    isInactiveFinDocRisk,
    isPreCasRisk,
    isInactivePreCasRisk,
    isUnlockVisaPrep,
    preCasFlags,
    getStudent,
    getAuditLog,
    onUpdateStatus,
    userName,
    displayName,
    onSubmitMessage,
    // onOverrideMaturedDate,
    onOverrideVisaStatus,
    onOverrideArrivalStatus,
    markMessagesRead,
    markMessageUnread,
    riskList,
    openedStudentRecords,
    onDownload,
    hasVisaPrepBeenActivated,
    config,
    auth,
    userRole,
    groups,
    visaAssistance,
    isAgent,
    showLastTouch,
    showArrivalStatus,
    travelStatusSection,
    showAgent,
    hasAgentMessages,
    isStudentLoaded,
    isPreDepartureDocsHidden,
    isAfterTravelDocsHidden,
    isPreCasFormHidden,
    isGeneralDocFormHidden,
    isFinanceDocFormHidden,
    isScheduledInterviewsHidden,
    isRecordedInterviewHidden,
    unpinTagsFromStudents,
    pinTagsToStudents,
    isPathway,
    sections,
    allTags,
    allProgrammableTags,
    calendlyInterviewsInfoStatus,
    recordedInterviewSystemStatus,
    processedByUni,
    allowedAliases,
    currentUser,
  } = props;
  const { core, personalInfo, offer, history, financials, summary, documents, importData, auditLog, emails } = student;
  const isStudentConfigLoaded = useSelector(({ data }: ApplicationState) => data.isStudentConfigLoaded);
  const intakeAfterCutoff = useSelector(selectIntakeAfterCutoff);

  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useLoadStudents();

  const { id = '', uni = '', intakeId = '', offerId = '' } = params;

  useEffect(() => {
    if (isStudentConfigLoaded) {
      dispatch(programmableTagsActions.getProgrammableTagRecords());
      if (initialMsgLoad) {
        setInitialMsgLoad(false);
        onLoadMessages();
      }
    }
    return () => {
      dispatch(setStudentConfigLoaded(false));
    };
  }, [isStudentConfigLoaded]);

  useEffect(() => {
    if (
      student.core.student_id.split('#')[1] !== id ||
      student.offer.intakeId !== intakeId ||
      student.offer.offerId !== offerId ||
      !isStudentLoaded
    ) {
      getStudent(id, uni, intakeId, offerId);
    }
  }, [
    id,
    intakeId,
    offerId,
    uni,
    getStudent,
    student.core.student_id,
    student.offer.intakeId,
    student.offer.offerId,
    isStudentLoaded,
  ]);

  const isLoadedForUrl =
    isStudentLoaded &&
    student.core.student_number === id &&
    student.offer.intakeId === intakeId &&
    student.offer.offerId === offerId;

  // Student docs are required to show correclty summary, particularly the Tier 4/SR time calc in the sidebar.
  useEffect(() => {
    if (isLoadedForUrl) {
      dispatch(getStudentDocs());
    }
  }, [dispatch, isLoadedForUrl]);

  const { registerViewIfNeeded } = useStudentViewRegistration([id, intakeId, offerId]);

  useEffect(() => {
    if (isLoadedForUrl) {
      const viewKey = `${id}#${intakeId}#${offerId}`;
      registerViewIfNeeded(viewKey, () => dispatch(registerStudentView()));
    }
  }, [id, intakeId, offerId, isLoadedForUrl, dispatch, registerViewIfNeeded]);

  // Get sidebar collapsed state from localStorage
  const { isOpen, onToggle } = useDisclosure({
    defaultIsOpen: (() => {
      try {
        return JSON.parse(localStorage.getItem('sidebarIsOpen') || 'true');
      } catch (error) {
        console.warn('Error reading sidebar state from localStorage:', error);
        return true; // Default to true if localStorage access fails
      }
    })(),
  });

  // Update localStorage whenever isOpen changes
  useEffect(() => {
    try {
      localStorage.setItem('sidebarIsOpen', JSON.stringify(isOpen));
    } catch (error) {
      console.warn('Error saving sidebar state to localStorage:', error);
    }
  }, [isOpen]);

  // Fetch ADM aliases for Admin/Enroly users
  const isAdminOrEnroly = userRole === UserRole.Admin || userRole === UserRole.Enroly;
  useEffect(() => {
    if (isAdminOrEnroly) {
      dispatch(getAdmAllowedAliases());
    }
  }, [isAdminOrEnroly, dispatch]);

  const [initialMsgLoad, setInitialMsgLoad] = useState(true);
  const [currentSection, setSection] = useState('info');
  const [previewOpen, toggleOpen] = useState(false);
  const [previewVisaPrep, setPreviewVisaPrep] = useState(false);
  const [editAgentAccess, setEditAgentAccess] = useState(false);
  const [editMaturedDate, setEditMaturedDate] = useState(false);
  const [editVisaStatus, setEditVisaStatus] = useState(false);
  const [editArrivalStatus, setEditArrivalStatus] = useState(false);
  const [editVisaPrepActive, setEditVisaPrepActive] = useState(false);
  const [trackerOpen, setTrackerOpen] = useState(false);
  const [auditLogOpen, setAuditLogOpen] = useState(false);
  const previewElement = useRef(null);
  const handlePrint = useReactToPrint({
    content: () => previewElement.current,
  });

  function onLoadMessages() {
    dispatch(getStudentMessages());
  }
  const showRisks = hasPerm(userRole, P.CHECKLIST_FLAGS);

  const toggleAuditLogOpenAndFetch = useCallback(() => {
    if (!auditLogOpen) {
      getAuditLog(id, uni, intakeId, offerId);
    }
    setAuditLogOpen(!auditLogOpen);
  }, [auditLogOpen, getAuditLog, id, intakeId, offerId, uni]);

  const handleSectionChange = useCallback(
    (id: string) => {
      setSection(id);
      if (id === 'audit-log') {
        toggleAuditLogOpenAndFetch();
      }
    },
    [toggleAuditLogOpenAndFetch],
  );

  function showContent(sectionId: string) {
    switch (sectionId) {
      case 'info':
        return <Info id={offerId} />;
      case 'checklist':
        return <Checklist />;
      case 'general-docs':
        return <Documents />;
      case 'financial-docs':
        return <FinanceDocuments />;
      case 'pre-departure-docs':
        return <BeforeTravelDocumentsDashboard />;
      case 'after-travel-docs':
        return <AfterTravelDocumentsDashboard />;
      case 'pre-cas':
        return <Interview id={offerId} />;
      case 'scheduled-interviews':
        return <ScheduledInterviews id={offerId} />;
      case 'visa-prep':
        return <VisaPrepForm id={offerId} />;
      case 'audit-log':
        return (
          <Auditlog key={'auditlog'} auth={auth} sortLogEntries={sortLogEntries} auditLog={auditLog} emails={emails} />
        );
      default:
        return null;
    }
  }

  const getT4VisaTime = () => {
    return getT4VisaTimeUtils(documents);
  };

  const getModal = (): React.ReactNode => {
    if (trackerOpen) {
      return <CASTracker onClose={() => setTrackerOpen(false)} summary={summary} />;
    }

    if (editVisaPrepActive) {
      return <VisaPrepActivate onClose={() => setEditVisaPrepActive(false)} />;
    }
    const currentImportOffer = importData.offers?.find(
      importOffer => importOffer.intakeId === offer.intakeId && importOffer.offerId === offer.offerId,
    );

    if (previewVisaPrep && config.sponsor && typeof config.sponsor === 'object') {
      const sponsorData = config.sponsor as any;
      const sponsor: ISponsorDetails = {
        licenceNumber: sponsorData.licenceNumber || '',
        schoolName: sponsorData.schoolName || '',
        address: sponsorData.address || '',
        city: sponsorData.city || '',
        postcode: sponsorData.postcode || '',
      };
      return (
        <Modal
          id={'preview-visa-prep'}
          show={true}
          title={`Student summary`}
          onClose={() => setPreviewVisaPrep(false)}
          hideFooter={true}
          withoutPadding={true}
        >
          {!student.visa && <>... No data entered</>}
          {student.visa && <VisaPrepPreview {...student} {...{ sponsor }} userRole={userRole} />}
        </Modal>
      );
    }

    if (previewOpen) {
      return (
        <Modal
          id={'preview-personal-info'}
          show={true}
          title={
            <Flex pr={3}>
              Personal Info
              <Button data-selector={'studentActions-downloadButton'} onClick={handlePrint} ml="auto" my={-1}>
                Download
              </Button>
            </Flex>
          }
          onClose={() => toggleOpen(false)}
        >
          <div ref={previewElement}>
            {!personalInfo && !history && !summary && <>... No data entered</>}
            {personalInfo && (
              <>
                <NameAndDOB
                  showRisks={showRisks}
                  surname={summary.surname}
                  middleName={summary.middleName}
                  givenName={summary.givenName}
                  hasOtherNames={boolToYesNo(personalInfo.hasOtherNames)}
                  otherNames={personalInfo.otherNames}
                  riskList={riskList.personalInfoRisk.namesAndAliases}
                />
                <NationalityAndSex
                  showRisks={showRisks}
                  nationality={summary.nationality}
                  countryOfBirth={summary.birthCountry}
                  livedInUKLast12Months={personalInfo.livedInUKLast12Months}
                  gender={summary.gender}
                  dob={summary.dob}
                  appliedForEUSS={personalInfo.appliedForEUSS}
                  grantedEUSSStatus={personalInfo.grantedEUSSStatus}
                  applyingFromHomeCountry={boolToYesNo(personalInfo.applyingFromHomeCountry)}
                  countryOfVisaApplication={personalInfo.countryOfVisaApplication}
                  otherCountryReason={personalInfo.otherCountryReason}
                  hasOtherNationality={boolToYesNo(personalInfo.hasOtherNationality)}
                  otherNationalities={personalInfo.otherNationalities}
                  riskList={riskList.personalInfoRisk.birthAndNationality}
                />
                <ContactAndAddress
                  showRisks={showRisks}
                  phoneNumber={personalInfo.phoneNumber}
                  address={personalInfo.homeAddress}
                  city={personalInfo.city}
                  postcode={personalInfo.postCode}
                  country={personalInfo.country}
                />
                <CriminalConvictionAndDisability
                  showRisks={showRisks}
                  hasCriminalConvictions={boolToYesNo(personalInfo.hasCriminalConvictions)}
                  criminalConvictionReason={personalInfo.criminalConvictionReason}
                  riskList={riskList.personalInfoRisk.criminalConvictions}
                />
                <CourseDetailsAndDeposit
                  courses={offer.courses}
                  useImportCourses={!student.offer.isPIPage5Complete}
                  showConditionStatusInCourseDetails={config.conditionStatusInCourseDetails}
                  showCourseConditionsInCourseDetails={config.courseConditionsInCourseDetails}
                  importCourses={currentImportOffer?.courses}
                  summaryCourses={summary.courses}
                  showOfferEvidence
                />
                <EnglishTestPI
                  showRisks={showRisks}
                  hasEnglishTest={boolToYesNo(personalInfo.hasEnglishTest)}
                  englishTestType={personalInfo.englishTestType}
                  englishTestTypeOther={personalInfo.englishTestTypeOther}
                  englishTestNotReqdReason={personalInfo.englishTestNotReqdReason}
                  englishTestNotReqdReasonOther={personalInfo.englishTestNotReqdReasonOther}
                  qualification={personalInfo.qualification}
                  qualificationOther={personalInfo.qualificationOther}
                  riskList={props.riskList.personalInfoRisk.englishTest}
                />
                <PassportDetails
                  showRisks={showRisks}
                  passportStatus={personalInfo.passportStatus}
                  passportExpected={personalInfo.passportExpectedDate}
                  passportExpiry={personalInfo.passportExpiryDate}
                  riskList={riskList.personalInfoRisk.passportDetails}
                />
                <EnrolmentSupport showRisks={showRisks} withAgent={personalInfo.withAgent} />
                <VisaSupport
                  config={student.config}
                  showRisks={showRisks}
                  visaSupport={boolToYesNo(offer.visaSupport)}
                />
                <WhatsApp
                  showRisks={showRisks}
                  useWhatsapp={personalInfo.useWhatsapp}
                  whatsAppPhoneNumber={personalInfo.whatsAppPhoneNumber}
                />
              </>
            )}
            {history && (
              <>
                <CustomHeading level="h4" mt={4}>
                  History
                </CustomHeading>
                <PriorStudyAndTravel
                  showRisks={showRisks}
                  lastCourse={history.lastCourse}
                  lastCourseOther={history.lastCourseOther}
                  lastCourseYear={history.lastCourseYear}
                  hasOverseasTravel={boolToYesNo(history.hasOverseasTravel)}
                  overseasTravelTimes={history.overseasTravelTimes}
                  hasUkTravel={boolToYesNo(history.hasUkTravel)}
                  ukTravelTimes={history.ukTravelTimes}
                  hasPreviousUkStudy={boolToYesNo(history.hasPreviousUkStudy)}
                  hasPreviousUkStudyVisa={boolToYesNo(history.hasPreviousUkStudyVisa)}
                  previousUkStudyVisas={history.previousUkStudyVisas}
                  lastCourseLevel={history.lastCourseLevel}
                  lowerOrSameLevelReason={history.lowerOrSameLevelReason}
                  riskList={props.riskList.historyRisk.priorStudyAndTravel}
                />
                <PriorTravelAndRefusals
                  showRisks={showRisks}
                  hasOtherUkVisas={boolToYesNo(history.hasOtherUkVisas)}
                  otherUkVisas={history.otherUkVisas}
                  hasLeaveToRemain={boolToYesNo(history.hasLeaveToRemain)}
                  hasBorderRefusal={boolToYesNo(history.hasBorderRefusal)}
                  borderRefusalReason={history.borderRefusalReason}
                  hasUkVisaRefusals={boolToYesNo(history.hasUkVisaRefusals)}
                  ukVisaRefusals={history.ukVisaRefusals}
                  hasOtherVisaRefusals={boolToYesNo(history.hasOtherVisaRefusals)}
                  otherVisaRefusals={history.otherVisaRefusals}
                  riskList={props.riskList.historyRisk.priorStudyAndRefusals}
                />

                <UpcomingVisaAndDependants
                  showRisks={showRisks}
                  hasDependents={boolToYesNo(offer.hasDependents)}
                  hasPartner={boolToYesNo(offer.hasPartner)}
                  partnerVisaStatus={String(offer.partnerVisaStatus)}
                  hasChildren={boolToYesNo(offer.hasChildren)}
                  childrenNum={String(offer.childrenNum)}
                  riskList={riskList.historyRisk.travellersAndDependants}
                />
              </>
            )}
            {financials && (
              <>
                <CustomHeading level="h4" mt={4}>
                  Finances
                </CustomHeading>
                <Finances
                  showRisks={showRisks}
                  organisationSponsorship={financials.organisationSponsorship ?? {}}
                  scholarshipSponsorship={financials.scholarshipSponsorship ?? {}}
                  educationSponsorship={financials.educationSponsorship ?? {}}
                  familySponsorship={financials.familySponsorship ?? {}}
                  otherSponsorship={financials.otherSponsorship ?? {}}
                  risk={riskList}
                  courseFeeDepositReqd={financials.courseFeeDepositReqd ?? ''}
                  courseFeeDepositDate={financials.courseFeeDepositDate ?? ''}
                  courseFeeDeposit={`£${formatCurrency(financials.courseFeeDeposit ?? 0)}`}
                  courseFeeDepositNotReqdReason={financials.courseFeeDepositNotReqdReason ?? ''}
                  accomDepositReqd={financials.accomDepositReqd ?? ''}
                  accomDepositDate={financials.accomDepositDate ?? ''}
                  accomDeposit={`£${formatCurrency(financials.accomDeposit ?? 0)}`}
                />
              </>
            )}
            {offer.assignedAgents?.length > 0 && student.config?.agentDetailsEnabled !== false && intakeAfterCutoff && (
              <>
                <CustomHeading level="h4" mt={4}>
                  Agent Details
                </CustomHeading>
                <AgentDetailsSummary
                  displayName={offer.assignedAgents[0].displayName ?? ''}
                  importAgentName={
                    offer.importAgentId === undefined || offer.importAgentId === offer.assignedAgents?.[0]?.id
                      ? (offer.importAgentName ?? undefined)
                      : undefined
                  }
                  agentFamilyName={offer.agentFamilyName ?? ''}
                  agentGivenName={offer.agentGivenName ?? ''}
                  branchAddressLine1={offer.branchAddressLine1 ?? ''}
                  branchAddressLine2={offer.branchAddressLine2 ?? ''}
                  branchAddressLine3={offer.branchAddressLine3 ?? ''}
                  branchCity={offer.branchCity ?? ''}
                  branchCountry={offer.branchCountry ?? ''}
                  branchPostcode={offer.branchPostcode ?? ''}
                />
              </>
            )}
          </div>
        </Modal>
      );
    }

    if (editMaturedDate) {
      return (
        <EditDateModal
          label="New Maturity Date"
          value={/* TODO overrideMaturedDate */ undefined}
          onUpdate={_date => {
            /* TODO
            setOverrideMaturedDate(date);
            onOverrideMaturedDate(date);
             */
          }}
          onClose={() => setEditMaturedDate(false)}
        />
      );
    }

    if (editVisaStatus) {
      const vStatus = summary.visaStatus as VisaStatus;
      return (
        <EditVisaStatusModal
          label="Update Visa Status"
          value={vStatus}
          visaStatuses={config.visaStatuses}
          onUpdate={(visaStatus: VisaStatus) => {
            onOverrideVisaStatus(visaStatus);
          }}
          onClose={() => setEditVisaStatus(false)}
        />
      );
    }

    if (editArrivalStatus) {
      return (
        <EditArrivalStatus
          label="Update Arrival Status"
          travelStatusSection={travelStatusSection}
          onUpdate={(travelStatusSection: IArrivalStatus) => {
            onOverrideArrivalStatus(travelStatusSection);
          }}
          onClose={() => setEditArrivalStatus(false)}
        />
      );
    }

    if (editAgentAccess && showAgent) {
      return <AgentAccessModal onClose={() => setEditAgentAccess(false)} />;
    }

    return null;
  };

  const onBackToStudentList = () => {
    let backUrl = '/';
    try {
      backUrl = sessionStorage.getItem(`cas-staff-uni-backUrl`) ?? '/';
      sessionStorage.removeItem('cas-staff-uni-backUrl');
    } catch (error) {
      console.warn('Error accessing sessionStorage:', error);
    }
    dispatch(closeStudent());
    if (backUrl !== '/') {
      navigate(`/${backUrl}`, { replace: true });
    } else {
      navigate('/');
    }
  };

  const canViewInsights = hasPerm(userRole, P.VIEW_INSIGHTS) || groups.includes('Insights');
  const canViewSettings = hasPerm(userRole, P.VIEW_CONFIG);

  const logOut = useCallback(() => dispatch(logout()), [dispatch]);
  const session = useSession();
  const onLogout = async () => {
    await session.onLogout();
    logOut();
  };

  // Build Act As options
  const currentUserName = useMemo(() => {
    return currentUser?.givenName && currentUser?.familyName
      ? `${currentUser.givenName} ${currentUser.familyName}`
      : userName || '';
  }, [currentUser, userName]);

  const actAsOptions = useMemo(() => {
    return allowedAliases && allowedAliases.length > 0 ? [currentUserName, ...allowedAliases].filter(Boolean) : [];
  }, [allowedAliases, currentUserName]);

  const currentAlias = getUserAlias() || currentUserName;

  const handleActAsChange = useCallback(
    (selectedAlias: string) => {
      if (selectedAlias === currentUserName) {
        removeUserAlias();
      } else {
        setUserAlias(selectedAlias);
      }
      // Reload the page to apply the alias change
      window.location.reload();
    },
    [currentUserName],
  );

  const studentNav = (
    <StudentLeftNav
      givenName={summary.givenName || undefined}
      middleName={summary.middleName || undefined}
      surname={summary.surname || undefined}
      studentNo={core.student_number}
      userRole={userRole}
      serviceProviderType={auth.serviceProviderType}
      onSectionChange={handleSectionChange}
      currentStage={(config?.moveToStages?.find((stage: any) => stage.key === offer.status)?.label ?? offer.status)!}
      infoState={infoState}
      checklistComplete={checklistComplete}
      docsComplete={docsComplete}
      docsRequired={docsRequired}
      finDocsComplete={finDocsComplete}
      finDocsRequired={finDocsRequired}
      beforeFollowUpDocsComplete={beforeFollowUpDocsComplete}
      beforeFollowUpDocsRequired={beforeFollowUpDocsRequired}
      afterFollowUpDocsComplete={afterFollowUpDocsComplete}
      afterFollowUpDocsRequired={afterFollowUpDocsRequired}
      interviewComplete={interviewComplete}
      visaPrepComplete={visaPrepComplete}
      isChecklistRisk={isChecklistRisk}
      isInactiveChecklistRisk={isInactiveChecklistRisk}
      isDocsRisk={isDocsRisk}
      isInactiveDocRisk={isInactiveDocRisk}
      isFinDocsRisk={isFinDocsRisk}
      isInactiveFinDocRisk={isInactiveFinDocRisk}
      isPreCasRisk={isPreCasRisk}
      isInactivePreCasRisk={isInactivePreCasRisk}
      preCasFlags={preCasFlags}
      hasVisaPrepBeenActivated={hasVisaPrepBeenActivated}
      visaPrepActiveForBrand={offerVisaSupport(
        student.offer,
        student.config,
        false,
        hasAgentExcludingEnroly(student.personalInfo, student.importData, student.offer),
      )}
      isPreCasFormHidden={isPreCasFormHidden}
      isGeneralDocFormHidden={isGeneralDocFormHidden}
      isFinanceDocFormHidden={isFinanceDocFormHidden}
      isPreDepartureDocsHidden={isPreDepartureDocsHidden}
      isAfterTravelDocsHidden={isAfterTravelDocsHidden}
      isScheduledInterviewsHidden={isScheduledInterviewsHidden}
      isRecordedInterviewHidden={isRecordedInterviewHidden}
      configSections={sections}
      calendlyInterviewsInfoStatus={calendlyInterviewsInfoStatus}
      recordedInterviewSystemStatus={recordedInterviewSystemStatus}
    />
  );

  const getDashboard = () => {
    const vStatus = summary.visaStatus as VisaStatus;
    const currentImportOffer = importData.offers?.find(
      importOffer => importOffer.intakeId === offer.intakeId && importOffer.offerId === offer.offerId,
    );
    return (
      <>
        <Navbar
          actionableButtonsStates={{
            [NavbarActionButtons.STUDENTS]: {
              isVisible: true,
              isSelected: true,
            },
            [NavbarActionButtons.INSIGHTS]: {
              isVisible: canViewInsights,
            },
            [NavbarActionButtons.SETTINGS]: {
              isVisible: canViewSettings,
            },
            [NavbarActionButtons.FEEDBACK]: {
              isVisible: true,
            },
          }}
          username={currentUserName}
          isAuthenticated={true}
          onLogout={onLogout}
          subnav={studentNav}
          actAs={
            actAsOptions.length > 1
              ? {
                  options: actAsOptions,
                  currentValue: currentAlias,
                  onChange: handleActAsChange,
                }
              : undefined
          }
        />

        <Flex
          ml={{ base: 0, md: isOpen ? 'sidebarWidth' : 'sidebarCollapse' }}
          transition={'ease-out 0.3s margin-left'}
        >
          <SidebarCollapse
            label="Student file navigation"
            isOpen={isOpen}
            onToggle={onToggle}
            containerProps={{
              position: 'fixed',
              left: 'navbarWidth',
              bottom: 0,
              top: 0,
              width: isOpen ? 'sidebarWidth' : 'sidebarCollapse',
              zIndex: 'docked',
            }}
          >
            {studentNav}
          </SidebarCollapse>

          <Flex flexGrow={1} pb={{ base: 'navbarHeight', md: 0 }} height={{ md: '100vh' }} overflow={{ md: 'hidden' }}>
            <MainColumn>
              <StudentHeader
                role={userRole}
                givenName={summary.givenName || undefined}
                middleName={summary.middleName || undefined}
                surname={summary.surname || undefined}
                studentNo={core.student_number}
                university={summary.uni}
                intakeId={offer.intakeId}
                offerId={offer.offerId}
                tags={offer.tags || []}
                allTags={allTags}
                allProgrammableTags={allProgrammableTags}
                isPathway={isPathway}
                unpinTagsFromStudents={unpinTagsFromStudents}
                pinTagsToStudents={pinTagsToStudents}
                onClose={onBackToStudentList}
                isPathwayStudent={Boolean(summary.pathway)}
                isPathwayProvider={(config?.pathwayIds?.length || 0) > 0}
                currentStage={
                  (config?.moveToStages?.find((stage: any) => stage.key === offer.status)?.label ??
                    offer.status) as Status
                }
                configTabs={config?.moveToStages.map((stage: any) => stage.label)}
                onStageChange={(status: Status) => {
                  const realStatus = config?.moveToStages?.find((stage: any) => stage.label === status)?.key ?? status;
                  onUpdateStatus(id, realStatus);
                }}
                processedByUni={processedByUni}
              />

              {props.isTrackerVisible ? (
                <CASTrackerReminder onClick={() => setTrackerOpen(true)} summary={summary} />
              ) : null}

              {showContent(currentSection)}
            </MainColumn>

            <AsideCollapse>
              <StudentSidebar
                arrivalStatus={travelStatusSection}
                pathway={summary.pathway ?? ''}
                lockAgent={lockAgent}
                courseName={summary.courses?.[0]?.title || undefined}
                lastAcceptanceDate={currentImportOffer?.courses?.[0]?.lastAcceptanceDate || undefined}
                email={core.email || undefined}
                lastTouch={summary.lastTouch}
                showLastTouch={showLastTouch}
                showArrivalStatus={showArrivalStatus}
                showAgent={showAgent}
                importAgent={currentImportOffer?.agentName || ''}
                assignedAgents={offer.assignedAgents}
                phone={personalInfo.phoneNumber || importData.phone}
                fundsMatured={summary.fundsMatured}
                nationality={summary.nationality || undefined}
                university={summary.uni}
                t4VisaTime={getT4VisaTime()}
                visaStatus={vStatus}
                currentStage={offer.status!}
                courseFeeDeposit={financials.courseFeeDeposit || currentImportOffer?.courseFeePaidToDate}
                expectedDepositDate={financials.courseFeeDepositDate}
                isConfidenceScoreView={isConfidenceScoreView}
                successPercentage={props.successPercentage}
                onEditAgentAccess={() => setEditAgentAccess(true)}
                onEditMaturedDate={() => setEditMaturedDate(true)}
                onEditVisaStatus={() => setEditVisaStatus(true)}
                onEditArrivalStatus={() => setEditArrivalStatus(true)}
                visaSupport={offer.visaSupport}
                applicantId={importData.applicantId}
              />

              <StudentActions
                role={userRole}
                visaPrepActiveForBrand={isUnlockVisaPrep}
                documentDownloadsActive={isDocsDownloadsActive}
                onPreview={() => toggleOpen(true)}
                onDownload={includeHidden => onDownload(includeHidden)}
                onUnlockVisaPrep={() => setEditVisaPrepActive(true)}
                onPreviewVisaPrep={() => setPreviewVisaPrep(true)}
                hasVisaPrepBeenActivated={hasVisaPrepBeenActivated}
                applicantId={importData.applicantId}
                university={summary.uni}
              />

              <AdminActions
                userRole={userRole}
                studentId={`${core.student_id}#${offer.intakeId}#${offer.offerId}`}
                currentImmigrationProvider={core.immigrationProviders?.length ? core.immigrationProviders[0] : null}
                immigrationProviders={config.immigrationProviders ?? []}
              />
            </AsideCollapse>
          </Flex>
        </Flex>
      </>
    );
  };

  return (
    <>
      <PageWrapper
        isLoading={isLoading || !isStudentLoaded}
        userName={userName}
        displayName={displayName}
        studentID={student.core.student_id.split('#')[1]}
        unreadMessagesCountToStudent={student.summary.unreadMessagesStudent}
        unreadMessagesCountFromStudent={student.summary.unreadMessagesUni}
        unreadMessagesUniToAgent={student.summary.unreadMessagesUniToAgent}
        unreadMessagesStudentToAgent={student.summary.unreadMessagesStudentToAgent}
        unreadMessagesAgentToUni={student.summary.unreadMessagesAgentToUni}
        unreadMessagesUniSecure={student.summary.unreadMessagesUniSecure}
        messages={student.messages}
        onSubmitMessage={onSubmitMessage}
        markMessagesRead={markMessagesRead}
        markMessageUnread={markMessageUnread}
        openedStudentRecords={openedStudentRecords}
        role={userRole}
        groups={groups}
        isAgent={isAgent}
        visaAssistance={visaAssistance}
        hasAgentMessages={hasAgentMessages}
        onLoadMessages={onLoadMessages}
      >
        {isStudentLoaded ? (
          <>
            <div id="content-loaded" />
            {getDashboard()}
          </>
        ) : null}
      </PageWrapper>

      {getModal()}
    </>
  );
}

function sortLogEntries(a: any, b: any) {
  const aTimeLog = a.hasOwnProperty('when') ? a['when'] : Math.floor((a?.['sentActionTime'] ?? 0) / 1000);
  const bTimeLog = b.hasOwnProperty('when') ? b['when'] : Math.floor((b?.['sentActionTime'] ?? 0) / 1000);

  if (aTimeLog < bTimeLog) {
    return -1;
  }
  if (aTimeLog > bTimeLog) {
    return 1;
  }
  return 0;
}

export default Dashboard;
